/**
 * PeriodSelector — 7D / 30D / 90D chip strip for pulse sparklines
 *
 * Renders three pill-shaped buttons above the pulse tile row.
 * Active chip: primary bg with white text. Inactive: transparent.
 * Right-aligned to match the tile row trailing edge.
 * Fully keyboard-navigable with aria-pressed.
 */

import type { PulsePeriod } from "@/hooks/usePulseSparkline";
import { cn } from "@/lib/utils";

const PERIODS: { value: PulsePeriod; label: string }[] = [
  { value: "7d", label: "7D" },
  { value: "mtd", label: "MTD" },
  { value: "30d", label: "30D" },
  { value: "90d", label: "90D" },
];

interface PeriodSelectorProps {
  value: PulsePeriod;
  onChange: (period: PulsePeriod) => void;
}

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  return (
    <div
      className="flex justify-end gap-1 mb-2"
      role="group"
      aria-label="Period selector"
    >
      {PERIODS.map((p) => {
        const isActive = p.value === value;
        return (
          <button
            key={p.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(p.value)}
            className={cn(
              "h-7 px-3 rounded-full text-xs font-medium",
              "transition-colors duration-150 ease-in-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
